import { useMemo } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import EntityImageDialog, { type EntityImageDialogCopy, type EntityImageDialogProps } from "../../EntityImage/EntityImageDialog";
import type { UseWorldbuildingImageGenerationResult, WorldbuildingBatchParams } from "./lib/useWorldbuildingImageGeneration";

type WorldbuildingImageDialogProps = Omit<
  EntityImageDialogProps<WorldbuildingBatchParams>,
  keyof UseWorldbuildingImageGenerationResult | "entityName" | "entityRoleLabel" | "fallbackIconSrc" | "imageNoun" | "copy" | "analytics" | "batchParams" | "resultAspectRatio"
> &
  UseWorldbuildingImageGenerationResult & {
    entryId: string;
    entryName: string;
    entryTypeLabel?: string | null;
  };

const COPY: EntityImageDialogCopy = {
  descriptionLabel: () => "Description",
  // Kept to one line so the composer stays at thumbnail height; the summarizer already reads the entry's AI-visible fields.
  descriptionPlaceholder: () => "We'll use this entry's Story Bible details. Add time of day, weather, or a viewpoint",
  emptyResultsText: "Your images will appear here",
  emptyResultsImageSrc: "/assets/worldbuilding-group.webp",
  emptyResultsImageClassName: "w-[380px] max-w-full",
  stylesPopoverWidth: "520px",
};

export default function WorldbuildingImageDialog({ entryId, entryName, entryTypeLabel, aspectRatio, tilesBatchParams, ...rest }: WorldbuildingImageDialogProps) {
  const analytics = useMemo(
    () => ({
      uploadFilesAdded: (props: { addedCount: number; totalPendingCount: number; rejectedDueToLimit: boolean }) =>
        void AppEventPublisher.worldbuildingImage.uploadFilesAdded({ worldbuildingEntryId: entryId, ...props }),
      uploadSaveClicked: (props: { fileCount: number }) => void AppEventPublisher.worldbuildingImage.uploadSaveClicked({ worldbuildingEntryId: entryId, ...props }),
    }),
    [entryId],
  );
  const batchParams = useMemo(() => ({ aspectRatio }), [aspectRatio]);

  return (
    <EntityImageDialog<WorldbuildingBatchParams>
      {...rest}
      tilesBatchParams={tilesBatchParams}
      entityName={entryName}
      entityRoleLabel={entryTypeLabel}
      fallbackIconSrc="/assets/world.svg"
      imageNoun="image"
      copy={COPY}
      analytics={analytics}
      batchParams={batchParams}
      // Tiles keep the ratio they were generated with, even if the entry's type changes afterwards.
      resultAspectRatio={tilesBatchParams?.aspectRatio ?? aspectRatio ?? undefined}
    />
  );
}
